import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { coachingApi } from '../../services/apiV2';

export interface PerformanceSelfReviewSummary {
  cycle_id: number;
  cycle_title: string;
  cycle_end_date?: string;
  goal_id: number;
  submitted: boolean;
  review_kind?: 'midpoint' | 'final';
  target_date?: string;
  opens_on?: string;
  available?: boolean;
  teacher_finalized?: boolean;
}

export function SelfReviewLinks({ reviews, sectionId, currentCycleId, compact = false, dueOnly = false }: {
  reviews: PerformanceSelfReviewSummary[]; sectionId: number; currentCycleId?: number; compact?: boolean; dueOnly?: boolean;
}) {
  const sorted = [...reviews]
    .filter(review => !currentCycleId || review.cycle_id === currentCycleId)
    .sort((a, b) => (a.target_date || a.cycle_end_date || '').localeCompare(b.target_date || b.cycle_end_date || ''));
  const actionable = sorted.find(review => review.available !== false && !review.submitted && !review.teacher_finalized);
  const current = actionable || sorted.find(review => !review.teacher_finalized) || sorted[sorted.length - 1];
  if (!current) return null;
  if (dueOnly && !actionable) return null;
  const card = (review: PerformanceSelfReviewSummary, featured = false) => <article key={`${review.cycle_id}:${review.review_kind || 'final'}`} className={`space-y-2 rounded-lg border p-4 ${featured ? 'border-l-4 border-brand bg-brand/5' : 'border-border'}`}>
    <p className="text-xs font-semibold uppercase tracking-wide text-muted">{review.review_kind === 'midpoint' ? 'Step 3 · Week 5' : 'Step 5 · End of cycle'}</p>
    <h3 className="font-semibold text-text">{review.review_kind === 'midpoint' ? 'Midpoint reflection' : 'Final self-review'} · {review.cycle_title}</h3>
    <p className="text-sm text-muted">{review.target_date ? `Target ${review.target_date}` : `Cycle ends ${review.cycle_end_date}`} · {review.teacher_finalized ? 'Review complete' : review.submitted ? 'Your reflection is submitted' : review.available === false ? `Opens ${review.opens_on}` : 'Ready for you'}</p>
    {review.available !== false && <Link className="inline-flex rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground" to={`/v2/student/reviews/${review.goal_id}?section=${sectionId}${review.review_kind === 'midpoint' ? '&review_kind=midpoint' : ''}`}>{review.submitted ? 'View my reflection' : review.review_kind === 'midpoint' ? 'Start midpoint reflection' : 'Start final self-review'}</Link>}
  </article>;
  const midpoint = sorted.find(review => review.review_kind === 'midpoint');
  const final = sorted.find(review => (review.review_kind || 'final') === 'final');
  const roadmap = <ol aria-label="Ten-week review cycle" className="grid grid-cols-5 gap-1 text-center text-[11px] text-muted">
    {[
      ['Goal', true],
      ['Week 3', false],
      ['Midpoint', Boolean(midpoint?.submitted || midpoint?.teacher_finalized)],
      ['Week 8', false],
      ['Final', Boolean(final?.submitted || final?.teacher_finalized)],
    ].map(([label, complete], index) => <li key={String(label)} className="relative pt-7"><span className={`absolute left-1/2 top-0 grid h-5 w-5 -translate-x-1/2 place-items-center rounded-full text-[10px] font-semibold ${complete ? 'bg-success text-white' : current.review_kind === (index === 2 ? 'midpoint' : index === 4 ? 'final' : '') ? 'bg-brand text-brand-foreground' : 'bg-surface-alt text-muted'}`}>{complete ? '✓' : index + 1}</span><span>{label}</span></li>)}
  </ol>;
  return <section aria-label="Performance review schedule" className="space-y-4 rounded-xl border border-brand/30 bg-surface p-5">
    <div><p className="text-xs font-semibold uppercase tracking-wide text-brand">{actionable ? 'Do this now' : 'Your review cycle'}</p><h2 className="text-lg font-semibold">{actionable ? 'Prepare for your performance conversation' : 'Know what is coming next'}</h2></div>
    {!compact && roadmap}
    <p className="text-sm text-muted">Your saved work is already connected. When a review opens, confirm the results, explain what the evidence shows, and prepare one next commitment.</p>
    {card(current, true)}
    {!compact && sorted.length > 1 && <details className="border-t border-border pt-3"><summary className="cursor-pointer text-sm font-medium">Completed and upcoming review steps</summary><div className="mt-3 space-y-4">{sorted.filter(review => review !== current).map(review => card(review))}</div></details>}
  </section>;
}

export function SelfReviewEntry({ sectionId, dueOnly = false }: { sectionId: number; dueOnly?: boolean }) {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let canceled = false;
    setData(null); setError(false);
    coachingApi.my(sectionId).then(result => { if (!canceled) setData(result); })
      .catch(() => { if (!canceled) setError(true); });
    return () => { canceled = true; };
  }, [sectionId]);
  if (error) return <Link className="text-brand underline" to={`/v2/student/coaching?section=${sectionId}`}>Find my self-review</Link>;
  return <SelfReviewLinks reviews={data?.review_schedule || data?.performance_self_reviews || []} sectionId={sectionId}
    currentCycleId={data?.progress_periods?.find((period: any) => period.state === 'active')?.id} compact dueOnly={dueOnly} />;
}
